import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { ConfirmSheet } from "../components/ConfirmSheet";
import { ExerciseSheet } from "../components/ExerciseSheet";
import { HowWasAsk } from "../components/HowWasIt";
import { MoveFigure } from "../components/MoveFigure";
import { PlayerShell, prevIcon, usePlayerOrigin } from "../components/PlayerShell";
import type { PlayerAction, PlayerMenuItem } from "../components/PlayerShell";
import { SegmentBar } from "../components/SegmentBar";
import { I, SESSION_ICON } from "../components/icons";
import { dayLabel, fmtClock, todayStr } from "../domain/date";
import { curWeight, sessionKcal } from "../domain/energy";
import { MET, doseIndex, metFor, sessionFor, sessionKeyFor } from "../domain/plan";
import { targetArea } from "../domain/exerciseMeta";
import { tr } from "../domain/exerciseHi";
import { finishedStats, halfwayCue, liveStats, minutesLeft, movesDone, playerMode, simpleIdle, simpleView, voiceCue } from "../domain/playerView";
import type { Ctl, CtlId } from "../domain/playerView";
import { programmeFor } from "../domain/programme";
import { quote } from "../domain/progress";
import { askTalk } from "../domain/readiness";
import { buildQueue, prepSecs, queueMinutes, ranFor, ranQueue, restRound, storeKey as makeStoreKey } from "../domain/queue";
import { streak, streakLine } from "../domain/stats";
import { isSteadyKey } from "../domain/templates";
import type { Exercise, QueueStep, SessionKey, WorkStep } from "../domain/types";
import { beep, buzz, cancelNotification, scheduleNotification, setAppBadge, setWakeLock } from "../lib/device";
import { useTimer } from "../lib/useTimer";
import { voice } from "../lib/voice";
import { touchDay } from "../store/dayLog";
import { useDeviceAnswers, useKaya, useTrainingDevice } from "../store/kayaStore";
import { sessionGate } from "../domain/trainingQuestions";
import { bankExtraClock, clearProgress, markRan, tickStep, untickFrom } from "../store/toggles";
import { usePlayerIntent } from "../store/playerIntent";

/** "set" is the get-set lead-in: on screen, on the clock, but not yet the move itself. */
type Stage = "idle" | "ready" | "set" | "work" | "workdone" | "rest" | "finished";

export default function Workout() {
  const history = useHistory();
  const { S, update, showToast, checkBadges, beginSession, endSession, session: rec, meta } = useKaya();
  // Simple, voice and language: the phone's own choice, else what the programme's answers set
  // (ADR-055). Hindi is the Simple player's; the Normal player stays English.
  const pm = playerMode(S.profile.training, meta);
  const simple = pm.simple;
  const lang = simple ? pm.lang : "en";
  const intent = usePlayerIntent();

  // The intent is consumed once, at mount: navigating back into a rescue must not
  // silently restart it as a normal session. With no intent, a workout record left by a
  // relaunch names the bucket instead, so a resumed rescue is still a rescue. (ADR-027)
  const [mode] = useState(() => {
    const rec = useKaya.getState().session;
    const left = rec?.kind === "workout" ? rec : null;
    if (intent.rescue || intent.extraKey) return { rescue: intent.rescue, extra: intent.extraKey, short: false };
    // the short version is the day's own session run short; a resumed one is still short (ADR-052)
    return { rescue: !!left?.rescue, extra: left?.extra ?? null, short: intent.short || !!left?.ran?.short };
  });
  const device = useTrainingDevice();
  const answers = useDeviceAnswers();
  // Minimise, Close and End go back where the player was started from: the rescue and the extra
  // session are started from Today, the day's own session from Train.
  const origin = usePlayerOrigin(mode.rescue || mode.extra ? "/today" : "/train");
  useEffect(() => {
    intent.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ds = todayStr();
  // The player runs only what the safety answers allow (P9, sessionGate): a held plan runs the
  // Chair session in place of anything else, fast work off runs Core & mobility in place of the
  // intervals, and a BP reading of 180/110 starts nothing — the rescue and the extras included.
  const gate = sessionGate(S, mode.rescue ? "T" : mode.extra ? mode.extra : sessionKeyFor(S, ds), answers);
  const key: SessionKey = gate.key;
  const p = doseIndex(S, key, ds);
  const bucket = makeStoreKey(key, {
    rescue: mode.rescue,
    extra: !!mode.extra,
  });

  // The day's own session runs what it froze — at Start in the open record, at the first tick in
  // the day log — so a resume after the phase moved or hip-safe changed walks the queue it began
  // and its ticks keep their meaning. The rescue and the extras are rebuilt as before. (ADR-050)
  const own = !mode.rescue && !mode.extra;
  const openRan = own && rec?.kind === "workout" && rec.ran?.key === key ? rec.ran : null;
  const dayRan = S.logs[ds]?.ran;
  const frozen = own ? openRan ?? (dayRan && dayRan.key === key ? dayRan : null) : null;
  // A Steady template is filled with the phone's safety answers, and run short when asked; a frozen
  // day rebuilds with the flags it froze (ADR-052).
  const fill = useMemo(
    () => (frozen ? { flags: frozen.flags, short: frozen.short } : { device, short: own && mode.short }),
    [frozen, device, own, mode.short]
  );
  const session = sessionFor(key, S, fill);
  const queue = useMemo<QueueStep[]>(
    () => (frozen ? ranQueue(S, frozen) : buildQueue(S, key, p, fill)),
    [S, key, p, frozen, fill]
  );
  const work = useMemo(() => queue.filter((q) => q.kind !== "rest"), [queue]);

  const doneMap = S.logs[ds]?.done?.[bucket] ?? {};

  /** First step not already ticked off, skipping a rest we would otherwise resume into. */
  const firstOpen = () => {
    let i = 0;
    while (i < queue.length && doneMap[i]) i++;
    if (i < queue.length && queue[i].kind === "rest" && i > 0) i++;
    return i;
  };

  const [i, setI] = useState(firstOpen);
  const [stage, setStage] = useState<Stage>(() => (queue.length && firstOpen() >= queue.length ? "finished" : "idle"));
  const [paused, setPaused] = useState(false);
  const [sheetEx, setSheetEx] = useState<Exercise | null>(null);
  const [askReset, setAskReset] = useState(false);
  const [askStop, setAskStop] = useState(false);
  const [, forceTick] = useState(0);

  const activeSince = useRef(0);
  const lastElapsed = useRef(0);
  const guardRef = useRef(0);

  const elapsedField = mode.rescue || mode.extra ? "extraElapsed" : "elapsedMs";

  const elapsedMs = useCallback(() => {
    const base = S.logs[ds]?.[elapsedField] || 0;
    return base + (activeSince.current ? Date.now() - activeSince.current : 0);
  }, [S.logs, ds, elapsedField]);

  /** 450 ms debounce on every control, as in the original — a double tap on a phone
   *  should not skip two steps. */
  const guard = () => {
    const n = Date.now();
    if (n - guardRef.current < 450) return false;
    guardRef.current = n;
    return true;
  };

  const elapsedStart = () => {
    if (!activeSince.current && !paused) activeSince.current = Date.now();
  };

  const elapsedStop = useCallback(() => {
    if (!activeSince.current) return;
    const delta = Date.now() - activeSince.current;
    activeSince.current = 0;
    update(
      (d) => {
        const L = touchDay(d, ds);
        L[elapsedField] = (L[elapsedField] || 0) + delta;
      },
      { silent: true },
    );
  }, [update, elapsedField, ds]);

  const markDone = (idx: number) => {
    const started = useKaya.getState().session;
    update((d) => {
      if (own) markRan(d, ds, key, (started?.kind === "workout" && started.ran) || frozen || undefined);
      tickStep(d, ds, bucket, idx);
    });
  };

  /* ---------- timer ---------- */

  const onSecond = (s: number) => {
    // A lower pip during the lead-in, so a count-in is never mistaken for a move ending.
    if (s <= 3 && s > 0) beep(stage === "set" ? 660 : 880, 80, 0.1, S.profile.sound);
    const cur = queue[i];
    if (pm.voice && stage === "work" && cur?.kind === "time" && s === Math.ceil(cur.secs / 2) && halfwayCue(cur.secs, s * 1000)) {
      void voice.say(["halfway"], pm.lang, S.profile.sound);
    }
  };

  const timer = useTimer(onSecond, () => {
    if (stage === "set") {
      beginWork();
    } else if (stage === "work" && simple) {
      // Simple: no "Time's up" to answer — a long beep, the tick, and on to the next (ADR-055)
      beep(1046, 600, 0.22, S.profile.sound);
      buzz([150, 80, 150], S.profile.vibrate);
      markDone(i);
      advance();
    } else if (stage === "work") {
      beep(1046, 250, 0.2, S.profile.sound);
      buzz([150, 80, 150], S.profile.vibrate);
      setStage("workdone");
    } else if (stage === "rest") {
      beep(880, 200, 0.18, S.profile.sound);
      buzz(120, S.profile.vibrate);
      markDone(i);
      advance();
    }
  });

  /* ---------- transitions ---------- */

  const finish = useCallback(() => {
    setStage("finished");
    void setWakeLock(false);
    elapsedStop();

    update((d) => {
      const L = touchDay(d, ds);

      if (mode.extra) {
        lastElapsed.current = bankExtraClock(d, ds);
        const hrs = lastElapsed.current / 3600000;
        L.extraKcal = (L.extraKcal || 0) + Math.round(metFor(key) * curWeight(S) * Math.max(hrs, 0.15));
        L.extras = L.extras || [];
        if (!L.extras.includes(key)) L.extras.push(key);
        return;
      }

      if (mode.rescue) {
        lastElapsed.current = bankExtraClock(d, ds);
        const hrs = lastElapsed.current / 3600000;
        L.extraKcal = (L.extraKcal || 0) + Math.round(MET.T * curWeight(S) * Math.max(hrs, 0.2));
        if (d.rescueFor) {
          if (!d.logs[d.rescueFor]) d.logs[d.rescueFor] = { done: {}, treadmill: null };
          d.logs[d.rescueFor].rescued = true;
          d.rescueFor = null;
        }
        L.rescueDone = true;
        return;
      }

      L.completedAt = new Date().toISOString();
      L.sessionKey = key;
      const hrs = (L.elapsedMs || 0) / 3600000;
      // a session finished in under three minutes is almost certainly ticked-through,
      // so fall back to the plan's own estimate rather than trusting the clock
      L.kcal = hrs > 0.05 ? Math.round(metFor(key) * curWeight(S) * hrs) : sessionKcal(S, key, 1, queue);
    });

    if (!mode.extra && !mode.rescue) setAppBadge(0);
    beep(1318, 300, 0.2, S.profile.sound);
    checkBadges();
  }, [update, mode, key, S, elapsedStop, checkBadges, queue]);

  const advance = useCallback(() => {
    cancelNotification("rest");
    let j = i + 1;
    const map = S.logs[ds]?.done?.[bucket] ?? {};
    while (j < queue.length && map[j]) j++;

    if (j >= queue.length) {
      setI(j);
      finish();
      return;
    }

    setI(j);
    const st = queue[j];
    if (st.kind === "rest") {
      setStage("rest");
      timer.start(st.secs);
      const nx = queue.slice(j + 1).find((q) => q.kind !== "rest") as WorkStep | undefined;
      scheduleNotification("rest", timer.endsAt(), "Rest over", nx ? "Next: " + nx.ex.n : "Next move");
    } else {
      setStage("ready");
    }
    elapsedStart();
  }, [i, queue, bucket, S.logs, ds, finish, timer]);

  /** Starts the move's own clock. Reached either straight from Start, or when the lead-in ends. */
  const beginWork = () => {
    const st = queue[i];
    if (st.kind !== "time") return;
    setStage("work");
    beep(1046, 160, 0.16, S.profile.sound);
    buzz(80, S.profile.vibrate);
    timer.start(st.secs);
    scheduleNotification("rest", timer.endsAt(), "Time's up", st.ex.n + " done — mark it");
  };

  const startWork = () => {
    if (!guard()) return;
    enterSet();
  };

  const enterSet = () => {
    const st = queue[i];
    if (st.kind !== "time") return;

    setPaused(false);
    void setWakeLock(true);
    elapsedStart();

    // Get into position first. The clock used to start on the press, so anything performed on the
    // floor lost its opening seconds to getting down there — or had to be set up before pressing
    // Start, with the phone already out of reach.
    const lead = prepSecs(S, simple);
    if (lead > 0) {
      setStage("set");
      timer.start(lead);
    } else {
      beginWork();
    }
  };

  const finishWork = () => {
    if (!guard()) return;
    timer.stop();
    cancelNotification("rest");
    markDone(i);
    advance();
  };

  const prevStep = () => {
    if (!guard()) return;
    timer.stop();
    cancelNotification("rest");
    let j = i - 1;
    while (j >= 0 && queue[j].kind === "rest") j--;
    if (j < 0) {
      showToast("Already at the first move");
      return;
    }
    // redoing a move invalidates everything after it
    update((d) => untickFrom(d, ds, bucket, j));
    setI(j);
    setStage("ready");
    setPaused(false);
    elapsedStart();
  };

  const togglePause = () => {
    if (paused) {
      setPaused(false);
      elapsedStart();
      if (stage === "set" || stage === "work" || stage === "rest") timer.resume();
    } else {
      setPaused(true);
      elapsedStop();
      if (stage === "set" || stage === "work" || stage === "rest") {
        timer.pause();
        cancelNotification("rest");
      }
    }
  };

  const adjustRest = (delta: number) => {
    if (stage !== "rest") return;
    const nl = Math.max(3000, timer.remaining + delta * 1000);
    timer.start(nl / 1000);
  };

  const reset = () => {
    setAskReset(false);
    update((d) => clearProgress(d, ds, key));
    setI(0);
    setStage("idle");
    if (useKaya.getState().session?.kind === "workout") endSession();
  };

  /* ---------- effects ---------- */

  // Simple: a timed move needs no Start — the 20 s get-set begins as the move comes up.
  useEffect(() => {
    if (!simple || stage !== "ready" || paused || queue[i]?.kind !== "time") return;
    enterSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simple, stage, paused, i]);

  // The voice says each step once, as it comes up: "next" and the move, "rest", "stand up slowly",
  // "switch sides", and "well done" at the finish. Off unless the phone asked for it.
  const spoken = useRef("");
  useEffect(() => {
    if (!pm.voice || stage === "idle") return;
    const at = stage === "finished" ? "end" : String(i);
    if (spoken.current === at) return;
    const ids = stage === "finished" ? ["well-done"] : stage === "rest" || stage === "ready" || stage === "set" ? voiceCue(queue, i) : null;
    if (!ids) return;
    spoken.current = at;
    void voice.say(ids, pm.lang, S.profile.sound);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, i, pm.voice, pm.lang]);

  // keep the elapsed readout ticking while a session is open
  useEffect(() => {
    const iv = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    setAppBadge(key !== "R" && stage !== "finished" ? 1 : 0);
  }, [key, stage]);

  // One session at a time: reached with a walk or treadmill run open, hand over to it.
  useEffect(() => {
    const rec = useKaya.getState().session;
    if (rec && rec.kind !== "workout") history.replace(rec.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The record that lets Home say "Session in progress" and the app reopen here. A workout keeps
  // its clock and its ticks in the day log, as it always has; the record only names the bucket
  // and is opened the moment the first move starts. Only when no record at all is open — another
  // kind's record is never overwritten from here. (ADR-027)
  useEffect(() => {
    const rec = useKaya.getState().session;
    if (stage === "finished") {
      if (rec?.kind === "workout") endSession();
    } else if (stage !== "idle" && !rec) {
      beginSession({
        kind: "workout",
        route: "/workout",
        startedAt: new Date().toISOString(),
        since: null,
        rescue: mode.rescue,
        extra: mode.extra,
        ...(own ? { ran: frozen ?? ranFor(useKaya.getState().S, key, Date.now(), { device, short: mode.short }) } : {}),
      });
    }
  }, [stage, mode, beginSession, endSession, own, frozen, key, device]);

  useEffect(
    () => () => {
      void setWakeLock(false);
      cancelNotification("rest");
      elapsedStop();
    },
    [elapsedStop],
  );

  /* ---------- render ---------- */

  const st = queue[i];
  const stepNo = queue.slice(0, i + 1).filter((q) => q.kind !== "rest").length;
  const next = queue.slice(i + 1).find((q) => q.kind !== "rest") as WorkStep | undefined;

  // `replace`: a pushed page stays mounted in Ionic, timers and all, and is handed back for the
  // next push to the same path. Minimise leaves the session open; the LiveBar takes over.
  const minimise = () => history.replace(origin);

  /** End, from the More menu once a move has started. A workout never told the counter app it
   *  started, so there is no `stop` to send: closing the record is the whole of it, and the ticks
   *  stay in the day log so the next Start picks up where this one left off. */
  const endHere = () => {
    timer.stop();
    cancelNotification("rest");
    // Back to the start screen first: Ionic keeps this page mounted, and a live stage with no
    // record is exactly what the record effect below reopens — End used to leave the session open.
    setStage("idle");
    setPaused(false);
    elapsedStop();
    if (useKaya.getState().session?.kind === "workout") endSession();
    history.replace(origin);
  };

  const sound: PlayerMenuItem = {
    label: "Sound",
    checked: S.profile.sound,
    onClick: () =>
      update((d) => {
        d.profile.sound = !d.profile.sound;
      }),
  };

  const nextText = next
    ? `Next · ${next.ex.n}${next.kind === "time" && next.sideLabel ? ` (${next.sideLabel})` : ""} · ${
        next.kind === "time" ? `${next.secs} s` : `${next.reps} reps${next.side ? " each side" : ""}`
      }`
    : null;

  // Estimated like the finished figure, from the same MET and the clock so far.
  const met = mode.rescue ? MET.T : metFor(key);
  const liveKcal = Math.round((met * curWeight(S) * elapsedMs()) / 3600000);
  const frac = timer.total ? 1 - timer.remaining / (timer.total * 1000) : 0;
  // A Steady person never sees kcal in the player (plan §5): minutes left instead.
  const steady = isSteadyKey(key) || programmeFor(S, ds) === "steady";
  const curStep = queue[i];
  const clockLeft =
    stage === "rest" || stage === "work"
      ? timer.remaining / 1000
      : stage === "set" && curStep?.kind === "time"
        ? timer.remaining / 1000 + curStep.secs
        : undefined;
  const statsFor = (screen: "move" | "rest", round: { round: number; rounds: number } | null) =>
    liveStats({
      simple,
      steady,
      lang,
      screen,
      stepNo,
      total: work.length,
      done: movesDone(queue, i),
      round,
      kcal: liveKcal,
      minsLeft: minutesLeft(S, queue, i, simple, clockLeft),
    });

  const prev: PlayerAction = {
    label: "Previous",
    ariaLabel: "Previous move",
    icon: prevIcon,
    onClick: prevStep,
  };
  const pauseCtl = {
    label: paused ? "Resume" : "Pause",
    icon: paused ? I.play : I.pause,
    onClick: togglePause,
  };
  const end =
    queue.length && stage !== "idle"
      ? {
          label: "End session",
          question: "End this session now? Moves you have done stay ticked.",
          onConfirm: endHere,
        }
      : null;

  const cur = queue[i];
  const curWork = cur && cur.kind !== "rest" && cur.ex.id === sheetEx?.id ? cur : null;
  const sheet = sheetEx ? (
    <ExerciseSheet
      ex={sheetEx}
      hipSafe={S.profile.hipSafe}
      note={curWork ? [curWork.cue, curWork.note].filter(Boolean).join(" · ") || undefined : undefined}
      onClose={() => setSheetEx(null)}
    />
  ) : null;

  const common = {
    title: session.name,
    onMinimise: minimise,
    elapsed: fmtClock(elapsedMs()),
    end,
    children: sheet,
  };

  if (gate.stop) {
    return (
      <PlayerShell
        surface="ink"
        title="No session today"
        onMinimise={minimise}
        menu={[sound]}
        name="No session today"
        cue={gate.line ?? undefined}
      />
    );
  }

  if (!queue.length) {
    return (
      <PlayerShell
        surface="ink"
        title="Rest day"
        onMinimise={minimise}
        menu={[sound]}
        name="Nothing to run today"
        cue="Change today's session on Train if you want to move."
      />
    );
  }

  if (stage === "finished") {
    const L = S.logs[ds] || { done: {}, treadmill: null };
    const kcal = mode.rescue || mode.extra ? L.extraKcal || 0 : L.kcal || sessionKcal(S, key, 1, queue);
    const mins = Math.round(
      ((mode.rescue || mode.extra ? lastElapsed.current : L.elapsedMs || queueMinutes(S, queue) * 60000) || 0) / 60000,
    );
    const fin = finishedStats({ simple, steady, lang, mins, kcal, streak: streak(S), moves: work.length });
    const finWords = simple ? { more: tr(lang, "More"), on: tr(lang, "On"), off: tr(lang, "Off"), lang } : null;
    return (
      <>
        <PlayerShell
          surface="done"
          title={`${tr(lang, session.name)} · ${dayLabel(ds)}`}
          onMinimise={minimise}
          simple={finWords}
          finished={{
            heading: mode.rescue
              ? "Streak repaired"
              : mode.extra
                ? "Extra session done"
                : tr(lang, simple ? "Well done" : "Session complete"),
            stats: fin.stats,
            line: fin.streakLine ? streakLine(streak(S)) : null,
            quote: simple ? undefined : quote(),
            onDone: () => history.replace("/today"),
            doneLabel: tr(lang, "Done"),
            secondary: mode.rescue || mode.extra ? null : { label: "Reset", onClick: () => setAskReset(true) },
          }}
        />
        {own && !askReset ? (
          <HowWasAsk
            ds={ds}
            sessionKey={key}
            short={mode.short}
            talk={askTalk(S, ds, key)}
            sub={`${tr(lang, session.name)} · ${mins} ${lang === "hi" ? tr(lang, "minutes") : "min · one tap"}`}
            words={lang === "hi" ? (en: string) => tr(lang, en) : undefined}
          />
        ) : null}
        {askReset ? (
          <ConfirmSheet
            title="Clear today's progress for this session?"
            sub="The session goes back to not started."
            confirm={{ label: "Clear progress", onClick: reset }}
            onClose={() => setAskReset(false)}
          />
        ) : null}
      </>
    );
  }

  if (stage === "idle" && simple) {
    // the same figure Train shows for the same queue, from where it resumes (queueMinutes, P9)
    const idleMin = queueMinutes(S, queue.slice(i), true);
    const words = simpleIdle(lang, session.name, idleMin, work.length);
    return (
      <PlayerShell
        {...common}
        title={words.title}
        surface="ink"
        menu={[{ ...sound, label: tr(lang, "Sound") }]}
        simple={{ more: tr(lang, "More"), on: tr(lang, "On"), off: tr(lang, "Off"), lang }}
        ring={{
          pct: 0,
          glyph: I[SESSION_ICON[session.type]],
          figure: idleMin,
          unit: words.unit,
          label: words.label,
        }}
        cue={words.cue}
        right={{
          label: words.start,
          onClick: () => {
            if (!guard()) return;
            beep(880, 80, 0.1, S.profile.sound);
            setStage("ready");
            void setWakeLock(true);
            elapsedStart();
          },
        }}
      />
    );
  }

  if (stage === "idle") {
    return (
      <PlayerShell
        {...common}
        surface="ink"
        menu={[sound]}
        ring={{
          pct: 0,
          glyph: I[SESSION_ICON[session.type]],
          figure: queueMinutes(S, queue, false),
          unit: `minutes · ${work.length} moves`,
          label: `${queueMinutes(S, queue, false)} minutes, ${work.length} moves`,
        }}
        cue="Timed moves and rests run on their own. Tap Done for reps. Rests can be skipped."
        right={{
          label: "Start",
          icon: I.play,
          onClick: () => {
            if (!guard()) return;
            beep(880, 80, 0.1, S.profile.sound);
            setStage("ready");
            void setWakeLock(true);
            elapsedStart();
          },
        }}
      />
    );
  }

  const segments = (current: number, progress: number) => (
    <SegmentBar count={work.length} current={current} progress={progress} onSurface />
  );

  if (simple) {
    const v = simpleView({
      lang,
      title: session.name,
      queue,
      i,
      stage: stage === "ready" || stage === "set" || stage === "work" || stage === "workdone" ? stage : "rest",
      paused,
      remainingMs: timer.remaining,
      totalSecs: timer.total,
    });
    const skipRest = () => {
      if (!guard()) return;
      timer.stop();
      cancelNotification("rest");
      markDone(i);
      advance();
    };
    const run: Record<CtlId, () => void> = {
      stop: () => setAskStop(true),
      pause: togglePause,
      resume: togglePause,
      done: finishWork,
      next: finishWork,
      skipRest,
      redo: () => {
        if (!guard()) return;
        setStage("ready");
      },
    };
    const icon: Partial<Record<CtlId, JSX.Element>> = { pause: I.pause, resume: I.play, done: I.tick, redo: I.refresh };
    const side = (c: Ctl): PlayerAction => ({ label: c.label, onClick: run[c.id] });
    const isRest = st.kind === "rest";
    return (
      <>
        <PlayerShell
          {...common}
          title={v.title}
          surface={paused ? "ink" : isRest ? "rest" : stage === "work" || st.kind === "reps" ? "work" : "ink"}
          end={null}
          // Previous and the move's sheet behind More, in words (board PlayerSimpleWork)
          menu={v.menu
            .filter((m) => !(isRest && m.id === "how"))
            .map((m): PlayerMenuItem =>
              m.id === "prev"
                ? { label: m.label, onClick: prevStep }
                : m.id === "how"
                  ? { label: m.label, onClick: () => setSheetEx((st as WorkStep).ex) }
                  : { ...sound, label: m.label },
            )}
          simple={{
            more: v.more,
            on: v.on,
            off: v.off,
            lang,
            sub: v.sub,
            large: v.large,
            nameLang: v.nameLang,
            subLang: v.subLang,
            cueLang: v.cueLang,
            figure: isRest ? null : <MoveFigure id={(st as WorkStep).ex.id} paper />,
          }}
          name={v.name}
          cue={v.cue}
          ring={{ ...v.ring, motion: paused ? null : isRest ? "breathe" : stage === "work" ? "pulse" : null }}
          adjust={
            isRest ? (
              <>
                <button
                  type="button"
                  className="k-player__btn k-player__btn--adj"
                  aria-label="Shorten rest by 15 seconds"
                  onClick={() => adjustRest(-15)}
                >
                  <span>{`−15 ${lang === "hi" ? tr(lang, "seconds") : "s"}`}</span>
                </button>
                <button
                  type="button"
                  className="k-player__btn k-player__btn--adj"
                  aria-label="Lengthen rest by 15 seconds"
                  onClick={() => adjustRest(15)}
                >
                  <span>{`+15 ${lang === "hi" ? tr(lang, "seconds") : "s"}`}</span>
                </button>
              </>
            ) : null
          }
          next={v.next}
          stats={statsFor(isRest ? "rest" : "move", null)}
          left={side(v.left)}
          centre={{ label: v.centre.label, icon: icon[v.centre.id] ?? I.tick, onClick: run[v.centre.id] }}
          right={side(v.right)}
        />
        {askStop ? (
          <ConfirmSheet
            title={tr(lang, "Stop the session?")}
            sub={tr(lang, "Moves you have done stay ticked.")}
            confirm={{
              label: tr(lang, "Stop"),
              icon: null,
              onClick: () => {
                setAskStop(false);
                endHere();
              },
            }}
            cancel={tr(lang, "Keep going")}
            onClose={() => setAskStop(false)}
          />
        ) : null}
      </>
    );
  }

  if (st.kind === "rest") {
    // The breather between two blocks belongs to neither block's rounds; `restRound` counts it
    // toward the block it leads into.
    const round = restRound(queue, i);
    const label = st.label
      ? `${st.label} · ${st.block}`
      : st.roundEnd
        ? `Round ${st.round} of ${st.rounds} done · ${st.block}`
        : st.blockEnd
          ? `${st.block} done — breathe`
          : `Rest · ${st.block} · round ${st.round}/${st.rounds}`;

    return (
      <PlayerShell
        {...common}
        surface={paused ? "ink" : "rest"}
        menu={[sound]}
        segments={segments(stepNo, 0)}
        line={paused ? `Paused · ${label}` : label}
        ring={{
          pct: frac,
          figure: fmtClock(timer.remaining),
          unit: paused ? "paused" : `rest left of ${fmtClock(timer.total * 1000)}`,
          label: `${fmtClock(timer.remaining)} of rest left`,
          motion: paused ? null : "breathe",
        }}
        adjust={
          <>
            <button
              type="button"
              className="k-player__btn k-player__btn--adj"
              aria-label="Shorten rest by 15 seconds"
              onClick={() => adjustRest(-15)}
            >
              <span>−15 s</span>
            </button>
            <button
              type="button"
              className="k-player__btn k-player__btn--adj"
              aria-label="Lengthen rest by 15 seconds"
              onClick={() => adjustRest(15)}
            >
              <span>+15 s</span>
            </button>
          </>
        }
        name={st.label ?? (st.roundEnd ? `Round ${st.round} complete` : "Breathe")}
        cue={
          st.label
            ? "Sit a moment, then stand with a hand on the counter until you feel steady."
            : st.roundEnd
              ? `${(st.rounds || 0) - (st.round || 0)} more to go`
              : null
        }
        next={nextText}
        stats={statsFor("rest", round)}
        left={prev}
        centre={pauseCtl}
        right={{
          label: "Skip rest",
          icon: I.skip,
          onClick: () => {
            if (!guard()) return;
            timer.stop();
            cancelNotification("rest");
            markDone(i);
            advance();
          },
        }}
      />
    );
  }

  const w = st as WorkStep;
  const target = targetArea(w.ex.id);
  const stageWord =
    w.kind === "reps"
      ? "Reps"
      : stage === "set"
        ? "Get set"
        : stage === "work"
          ? "Work"
          : stage === "workdone"
            ? "Time's up"
            : "Ready";
  const line = `${paused ? "Paused" : stageWord} · ${w.block} · move ${w.idx} of ${w.total}`;
  const skipMove: PlayerMenuItem = {
    label: "Skip this move",
    onClick: finishWork,
  };
  const moveProps = {
    ...common,
    info: { label: "How to do this move", onClick: () => setSheetEx(w.ex) },
    segments: segments(stepNo - 1, w.kind === "time" && stage === "work" ? frac : 0),
    line,
    name: `${w.ex.n}${w.kind === "time" && w.sideLabel ? ` · ${w.sideLabel}` : ""}`,
    cue: `${target ? target + " · " : ""}${w.ex.how.split(". ")[0]}.${w.cue ? ` ${w.cue.charAt(0).toUpperCase()}${w.cue.slice(1)}.` : ""}`,
    next: nextText,
    stats: statsFor("move", { round: w.round, rounds: w.rounds }),
    left: prev,
  };

  let shell: JSX.Element;
  if (w.kind === "reps") {
    shell = (
      <PlayerShell
        {...moveProps}
        surface={paused ? "ink" : "work"}
        menu={[sound]}
        ring={{
          pct: 1,
          glyph: I[w.ex.t],
          figure: w.reps,
          unit: `reps${w.side ? " each side" : ""}${paused ? " · paused" : ""}`,
          label: `${w.reps} reps${w.side ? " each side" : ""}`,
        }}
        centre={pauseCtl}
        right={{ label: "Done", icon: I.tick, onClick: finishWork }}
      />
    );
  } else if (stage === "ready") {
    shell = (
      <PlayerShell
        {...moveProps}
        surface="ink"
        menu={[sound, skipMove]}
        ring={{
          pct: 0,
          figure: fmtClock((w.secs || 0) * 1000),
          unit: paused ? "paused" : "timed",
          label: `${fmtClock((w.secs || 0) * 1000)} timed move`,
        }}
        note={prepSecs(S) > 0 ? `${prepSecs(S)} s to get into position first.` : null}
        centre={pauseCtl}
        right={{ label: "Start", icon: I.play, onClick: startWork }}
      />
    );
  } else if (stage === "set") {
    shell = (
      <PlayerShell
        {...moveProps}
        surface="ink"
        menu={[sound, skipMove]}
        ring={{
          pct: frac,
          figure: Math.ceil(timer.remaining / 1000),
          unit: paused ? "paused" : "get set",
          label: `${Math.ceil(timer.remaining / 1000)} seconds to get set`,
        }}
        note={`${w.secs} s starts when this reaches zero${w.sideLabel ? ` — ${w.sideLabel}` : ""}.`}
        centre={pauseCtl}
        right={{ label: "Start now", icon: I.play, onClick: beginWork }}
      />
    );
  } else if (stage === "work") {
    shell = (
      <PlayerShell
        {...moveProps}
        surface={paused ? "ink" : "work"}
        menu={[sound]}
        ring={{
          pct: frac,
          figure: fmtClock(timer.remaining),
          unit: paused ? "paused" : `left of ${fmtClock((w.secs || 0) * 1000)}`,
          label: `${fmtClock(timer.remaining)} left of ${fmtClock((w.secs || 0) * 1000)}`,
          motion: paused ? null : "pulse",
        }}
        centre={pauseCtl}
        right={{ label: "Done", icon: I.tick, onClick: finishWork }}
      />
    );
  } else {
    shell = (
      <PlayerShell
        {...moveProps}
        surface="done"
        menu={[sound]}
        ring={{ pct: 1, figure: "0:00", unit: "time's up", label: "Time's up" }}
        centre={{
          label: "Redo move",
          icon: I.refresh,
          onClick: () => {
            if (!guard()) return;
            setStage("ready");
          },
        }}
        right={{ label: "Mark done", icon: I.tick, onClick: finishWork }}
      />
    );
  }

  return shell;
}
